import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { NavigateLinks } from '@/components';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { childrenBusinessPageBreadcrumbs } from '../../config/breadcrumbs';
import BillingReportsTable from './components/BillingReportsTable';
import type { BillingReport } from './models/BillingReport';
import {
  useGetBillingReportsQuery,
  useLazyGetBillingReportDownloadUrlQuery,
} from './services/billingReports.service';

const PAGE_SIZE = 24;

const BillingReports = () => {
  const navigate = useNavigate();
  const breadcrumbs = useMemo(() => childrenBusinessPageBreadcrumbs(navigate), [navigate]);
  const { showNotification } = useNotification();
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data, isFetching } = useGetBillingReportsQuery(
    { page, pageSize: PAGE_SIZE },
    { refetchOnMountOrArgChange: true }
  );
  const [getDownloadUrl] = useLazyGetBillingReportDownloadUrlQuery();

  const handleDownload = async (report: BillingReport) => {
    setDownloadingId(report.id);
    try {
      const { url } = await getDownloadUrl(report.id).unwrap();
      window.location.assign(url);
    } catch {
      showNotification('No se pudo descargar el reporte.', NotificationSeverity.error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className='flex flex-col gap-3 w-full h-full min-h-0'>
      <NavigateLinks actualPage='Facturación' breadcrumbs={breadcrumbs} />

      <Alert severity='info' icon={<InfoIcon fontSize='small' />} sx={{ borderRadius: 2 }}>
        Pedidos entregados por quincena, del 1 al 15 y del 16 a fin de mes. El reporte de cada
        quincena se genera solo el día 1 y el 16 a las 3 de la mañana.
      </Alert>

      <BillingReportsTable
        reports={data?.items ?? []}
        isLoading={isFetching}
        totalItems={data?.count ?? 0}
        currentPage={page}
        pageSize={PAGE_SIZE}
        downloadingId={downloadingId}
        onPageChange={setPage}
        onDownload={handleDownload}
      />
    </section>
  );
};

export default BillingReports;
